/**
 * EXPERIMENT ONLY — no production counterpart. Renders only when `isBillingExperimentOn()` is true.
 *
 * Asked when the wizard's corner X is pressed on the Payment step. A local dialog rather than the
 * shared `useConfirm`: that one has no corner X and a fixed width, and changing it would restyle
 * every confirm in the app. Staying is the primary (green) action; the corner X, Esc and
 * "Back to Checkout" all stay, and only "Leave" leaves.
 */
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogClose,
  Button,
} from '@scio3/ui';

type Props = {
  open: boolean;
  onStay: () => void;
  onLeave: () => void;
};

export function LeaveCheckoutDialog({ open, onStay, onLeave }: Props) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onStay();
      }}
    >
      {/* `sm:max-w-sm` keeps it close to square; the shared confirm is a wide, short strip. */}
      <DialogContent className="gap-0 p-6 sm:max-w-sm" data-testid="leave-checkout-dialog">
        {/* `pr-8` clears the corner X. */}
        <DialogTitle className="pr-8 text-xl font-semibold">
          {t('Leave without subscribing?')}
        </DialogTitle>
        <DialogDescription className="mt-2 text-sm text-muted-foreground">
          {t("If you leave now, you'll need to choose your plan and screen license again.")}
        </DialogDescription>
        {/* Back to Checkout left, Leave right: product call (2026-09-24), not the usual
            primary-on-the-right order. */}
        <div className="mt-6 flex items-center justify-end gap-3">
          <DialogClose asChild>
            <Button data-testid="leave-checkout-stay-btn">{t('Back to Checkout')}</Button>
          </DialogClose>
          {/* "Leave", not "Close": echoes the title, so it is clear what is being left. */}
          <Button variant="outline" onClick={onLeave} data-testid="leave-checkout-leave-btn">
            {t('Leave')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
